import { ConstructionSiteFullDetails } from "@dune/types";
import { GFAFullDetails } from "@dune/types";
import { isSocietySyndic } from "@dune/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

export const userClientSchema = z.object({
	id: z.number().optional(),
	firstname: z.string().min(1, "Le prénom est obligatoire"),
	lastname: z.string().min(1, "Le nom de famille est obligatoire"),
	email: z.string().optional(),
	streetNumber: z.string().optional(),
	streetName: z.string().optional(),
	postalCode: z.string().optional(),
	city: z.string().optional(),
});

const userClientWithEmailSchema = userClientSchema.extend({
	email: z.string().min(1, "L'email est obligatoire"),
});

// Second step (MOA society): SIREN optional, 9 or 14 chars when provided
export const baseSocietyClientSchema = z.object({
	id: z.number().optional(),
	siren: z
		.string()
		.refine((val) => !val || val.length === 9 || val.length === 14, {
			message: "Le SIREN doit faire 9 ou le SIRET doit faire 14 caractères",
		})
		.nullable(),
	denomination: z.string().min(1, "La raison sociale est obligatoire"),
	streetNumber: z.string().optional(),
	streetName: z.string().min(1, "Le nom de rue est obligatoire"),
	postalCode: z.string().min(1, "Le code postal est obligatoire"),
	city: z.string().min(1, "La ville est obligatoire"),
});

export const societyClientSchema = baseSocietyClientSchema
	.extend({
		// Override siren: no length restriction when client is syndic (or through syndic)
		siren: z.string().nullable(),
		isSyndic: z.boolean().optional(),
		hasBrokerageFees: z
			.string()
			.refine((data) => data === "true" || data === "false", {
				message: "Le champ est invalide",
			})
			.optional(),
		hasUserAccess: z
			.string()
			.refine((data) => data === "true" || data === "false", {
				message: "Le champ est invalide",
			})
			.optional(),
		brokerageFees: z.string().optional(),
		userAccess: z
			.object({
				lastname: z.string(),
				firstname: z.string(),
				email: z.string(),
			})
			.optional(),
	})
	.superRefine((data, ctx) => {
		// When client is syndic: no SIREN requirement. When not syndic: SIREN mandatory and 9 or 14 chars
		if (data.isSyndic) return;
		const siren = data.siren?.trim();
		if (!siren) {
			ctx.addIssue({
				path: ["siren"],
				code: "custom",
				message: "Le SIREN est obligatoire",
			});
			return;
		}
		const isValidLength = siren.length === 9 || siren.length === 14;
		if (!isValidLength) {
			ctx.addIssue({
				path: ["siren"],
				code: "custom",
				message: "Le SIREN doit faire 9 ou le SIRET doit faire 14 caractères",
			});
		}
	})
	.superRefine((data, ctx) => {
		if (data.hasBrokerageFees === "true" && data.isSyndic) {
			if (data.brokerageFees === undefined) {
				ctx.addIssue({
					path: ["brokerageFees"],
					code: "custom",
					message: "Le montant des frais annexes est obligatoire",
				});
			}
		}
	})
	.superRefine((data, ctx) => {
		if (data.hasUserAccess === "true") {
			const userAccess = userClientWithEmailSchema.safeParse(data.userAccess);
			if (!userAccess.success) {
				for (const error of userAccess.error.issues) {
					ctx.addIssue({
						path: ["userAccess", ...error.path],
						code: "custom",
						message: error.message,
					});
				}
			}
		}
	});

const createConstructionSiteSchema = z
	.object({
		constructionSiteId: z.number().optional(),
		isGFA: z.boolean().optional(),
	})
	.and(
		z.discriminatedUnion("clientType", [
			z.object({
				clientType: z.literal("USER"),
				clientUsers: z
					.array(userClientSchema)
					.min(1, "Au moins un utilisateur est requis")
					.max(2, "Maximum 2 utilisateurs autorisés")
					.superRefine((users, ctx) => {
						const emails = users
							.map((user) => user.email)
							.filter((email) => email);
						if (emails.length !== new Set(emails).size) {
							ctx.addIssue({
								code: "custom",
								message: "Les emails doivent être uniques",
								path: ["clientUsers"],
							});
						}
					}),
			}),
			z.object({
				clientType: z.literal("SOCIETY"),
				clientSocietyId: z.number().optional(),
				clientSociety: societyClientSchema,
			}),
		]),
	)
	.and(
		z.union([
			z.object({
				clientIsMOA: z.literal("false"),
				moaClientType: z.literal("USER"),
				moaClientUserId: z.number().optional(),
				moaClientUser: z.object({
					firstname: z.string(),
					lastname: z.string(),
				}),
			}),
			z.object({
				clientIsMOA: z.literal("false"),
				moaClientType: z.literal("SOCIETY"),
				moaClientSocietyId: z.number().optional(),
				moaClientSociety: baseSocietyClientSchema,
			}),
			z.object({
				clientIsMOA: z.literal("true"),
			}),
		]),
	);

export type CreateConstructionSiteClientFormData = z.infer<
	typeof createConstructionSiteSchema
>;

export { createConstructionSiteSchema };

const createConstructionSiteDefaultValues: CreateConstructionSiteClientFormData =
	{
		clientType: "USER",
		clientIsMOA: "true",
		clientUsers: [
			{
				id: undefined,
				firstname: "",
				lastname: "",
				email: "",
				streetNumber: "",
				streetName: "",
				postalCode: "",
				city: "",
			},
		],
	};

const mapSelectedClientToFormValues = (
	constructionSite: ConstructionSiteFullDetails | GFAFullDetails,
): CreateConstructionSiteClientFormData => {
	const clientDetails = constructionSite.constructionSiteClientDetails;
	if (!clientDetails) return createConstructionSiteDefaultValues;

	const clientContacts = clientDetails.constructionSiteClientContacts || [];
	const clientSociety = clientDetails.clientSociety;

	const clientUsers =
		clientContacts.length > 0
			? clientContacts.map((contact) => ({
					id: contact.constructionSiteContact?.id ?? undefined,
					firstname: contact.constructionSiteContact?.firstname ?? "",
					lastname: contact.constructionSiteContact?.lastname ?? "",
					email: contact.constructionSiteContact?.email ?? "",
					streetNumber: "",
					streetName: "",
					postalCode: "",
					city: "",
				}))
			: [
					{
						id: undefined,
						firstname: "",
						lastname: "",
						email: "",
						streetNumber: "",
						streetName: "",
						postalCode: "",
						city: "",
					},
				];

	const isClientMOA = clientDetails.clientIsMOA ? "true" : "false";
	const clientType =
		clientDetails.clientType ||
		(clientContacts.length > 0 ? "USER" : "SOCIETY");
	const moaClientType =
		clientDetails.moaClientType ||
		(clientDetails.moaClientContact?.firstname ? "USER" : "SOCIETY");

	const clientSocietyPart = {
		id: clientSociety?.id ?? undefined,
		siren: clientSociety?.siren ?? null,
		denomination: clientSociety?.denomination ?? "",
		streetNumber: clientSociety?.headOfficeAddress?.streetNumber ?? "",
		streetName: clientSociety?.headOfficeAddress?.streetName ?? "",
		postalCode: clientSociety?.headOfficeAddress?.postalCode ?? "",
		city: clientSociety?.headOfficeAddress?.city ?? "",
		isSyndic:
			(clientSociety &&
				isSocietySyndic(clientSociety) &&
				clientSociety?.id === constructionSite.syndic?.id) ??
			false,
		hasUserAccess: clientSociety?.syndicUserAccessToPlatform ? "true" : "false",
		hasBrokerageFees: clientSociety?.brokerageFees ? "true" : "false",
		brokerageFees: clientSociety?.brokerageFees
			? clientSociety.brokerageFees.toString()
			: undefined,
		userAccess: clientSociety?.syndicUserAccessToPlatform
			? {
					lastname: clientSociety?.syndicUserAccessToPlatform.lastname,
					firstname: clientSociety?.syndicUserAccessToPlatform.firstname,
					email: clientSociety?.syndicUserAccessToPlatform.email ?? "",
				}
			: undefined,
	};

	const moaClientUserPart = {
		firstname: clientDetails.moaClientContact?.firstname ?? "",
		lastname: clientDetails.moaClientContact?.lastname ?? "",
		email: clientDetails.moaClientContact?.email ?? "",
	};

	const moaClientSocietyPart = {
		siren: clientDetails.moaClientSociety?.siren ?? null,
		denomination: clientDetails.moaClientSociety?.denomination ?? "",
		streetNumber:
			clientDetails.moaClientSociety?.headOfficeAddress?.streetNumber ?? "",
		streetName:
			clientDetails.moaClientSociety?.headOfficeAddress?.streetName ?? "",
		postalCode:
			clientDetails.moaClientSociety?.headOfficeAddress?.postalCode ?? "",
		city: clientDetails.moaClientSociety?.headOfficeAddress?.city ?? "",
	};

	//on passe moaClientType partout car le backend n'est pas config pour recevoir des données conditionnelles (à faire pour un meilleur typage)
	//le backend attendra toujours moaClientType même si clientIsMOA est true
	const base = {
		moaClientType,
	};

	if (clientType === "USER") {
		const baseClientTypeUser = {
			clientType,
			clientUsers,
			...base,
		};
		if (isClientMOA === "false") {
			return moaClientType === "SOCIETY"
				? {
						...baseClientTypeUser,
						clientIsMOA: isClientMOA,
						moaClientType,
						moaClientSociety: moaClientSocietyPart,
					}
				: {
						...baseClientTypeUser,
						clientIsMOA: isClientMOA,
						moaClientType,
						moaClientUser: moaClientUserPart,
					};
		}
		return { ...baseClientTypeUser, clientIsMOA: isClientMOA };
	}
	const baseclientTypeSociety = {
		clientType,
		clientSociety: clientSocietyPart,
		...base,
	};

	if (isClientMOA === "false") {
		return moaClientType === "SOCIETY"
			? {
					...baseclientTypeSociety,
					clientIsMOA: isClientMOA,
					moaClientType,
					moaClientSociety: moaClientSocietyPart,
				}
			: {
					...baseclientTypeSociety,
					clientIsMOA: isClientMOA,
					moaClientType,
					moaClientUser: moaClientUserPart,
				};
	}
	return {
		...baseclientTypeSociety,
		clientIsMOA: isClientMOA,
	};
};

export function useCreateConstructionSiteForm(
	constructionSite:
		| ConstructionSiteFullDetails
		| GFAFullDetails
		| null
		| undefined,
) {
	const defaultValues = constructionSite
		? mapSelectedClientToFormValues(constructionSite)
		: createConstructionSiteDefaultValues;
	const form = useForm<CreateConstructionSiteClientFormData>({
		mode: "all",
		defaultValues,
		resolver: zodResolver(createConstructionSiteSchema),
	});

	const { reset } = form;
	useEffect(() => {
		if (constructionSite)
			reset(mapSelectedClientToFormValues(constructionSite));
	}, [reset, constructionSite]);
	return form;
}
